import { validateGCompanyField } from '@opportunities/-lib/opportunityValidation'
import { useEffect, useState } from 'react'

interface UseGCompanyDialogParams {
  open: boolean
  opportunity: {
    id: number
    title: string
    g_company_name?: string | null
    g_company_contact?: string | null
  } | null
  onSubmit: (data: { gCompanyName: string; gCompanyContact: string }) => void
  onOpenChange: (open: boolean) => void
}

/**
 * GCompanyDialogのロジックを管理するカスタムhooks
 *
 * G会社商流情報の入力フォームの状態管理、バリデーション、送信処理を行います。
 */
export function useGCompanyDialog({ open, opportunity, onSubmit, onOpenChange }: UseGCompanyDialogParams) {
  const [gCompanyName, setGCompanyName] = useState('')
  const [gCompanyContact, setGCompanyContact] = useState('')
  // 一度でも編集されたかどうかのフラグ（初期表示時はエラーを表示しない）
  const [touched, setTouched] = useState({ name: false, contact: false })

  /**
   * ダイアログが開いたときにフォームを初期化
   */
  useEffect(() => {
    if (open && opportunity) {
      setGCompanyName(opportunity.g_company_name || '')
      setGCompanyContact(opportunity.g_company_contact || '')
      setTouched({ name: false, contact: false })
    }
  }, [open, opportunity])

  // バリデーション結果（レンダリング時に直接計算）
  const gCompanyNameError = touched.name ? validateGCompanyField(gCompanyName) : null
  const gCompanyContactError = touched.contact ? validateGCompanyField(gCompanyContact) : null

  // エラーがあるかどうか（表示中のエラーベースでチェック）
  const hasErrors = !!gCompanyNameError || !!gCompanyContactError

  /**
   * フォーム送信ハンドラー
   */
  const handleSubmit = () => {
    // 送信時に全フィールドを touched にする
    setTouched({ name: true, contact: true })

    if (!!validateGCompanyField(gCompanyName) || !!validateGCompanyField(gCompanyContact)) {
      return
    }

    onSubmit({
      gCompanyName: gCompanyName.trim(),
      gCompanyContact: gCompanyContact.trim(),
    })

    onOpenChange(false)
  }

  // 入力変更時に touched フラグを立てるラッパー
  const handleNameChange = (value: string) => {
    setGCompanyName(value)
    setTouched((prev) => ({ ...prev, name: true }))
  }

  const handleContactChange = (value: string) => {
    setGCompanyContact(value)
    setTouched((prev) => ({ ...prev, contact: true }))
  }

  return {
    gCompanyName,
    gCompanyContact,
    gCompanyNameError,
    gCompanyContactError,
    hasErrors,
    handleNameChange,
    handleContactChange,
    handleSubmit,
  }
}
